import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from "recharts";
import {
  ArrowLeft,
  ArrowUpRight,
  Sparkles,
  Youtube,
  Instagram,
  Twitch,
  Check,
  TrendingUp,
  Target,
  GraduationCap,
} from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingPdfButton from "../components/FloatingPdfButton";
import CountUp from "../components/CountUp";
import { TOC, getCaseStudyBySlug } from "../data/caseStudies";
import StickyToc from "../components/case-study/StickyToc";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHART_COLORS = ["#9D00FF", "#FF1493", "#00E5FF", "#E6FF00", "#A1A1AA"];

export default function CaseStudyPage() {
  const { slug } = useParams();

  // 1) Source of truth : local data → always renders instantly, even without backend
  const localCs = getCaseStudyBySlug(slug);
  const [cs, setCs] = useState(localCs);

  // 2) Background refresh from API (optional — silently ignored if backend is down)
  useEffect(() => {
    let mounted = true;
    if (!localCs) return; // unknown slug → 404 below
    (async () => {
      try {
        const { data } = await axios.get(`${API}/case-studies/${slug}`, {
          timeout: 5000,
        });
        // Only replace if the response is a real object, not an HTML fallback
        if (
          mounted &&
          data &&
          typeof data === "object" &&
          data.slug === slug &&
          Array.isArray(data.influencers)
        ) {
          setCs(data);
        }
      } catch {
        /* backend unreachable — keep local data */
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug, localCs]);

  if (!localCs) return <NotFoundState />;
  if (!cs) return <LoadingState />;

  return (
    <div data-testid="case-study-page" className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      {/* ========= HERO ========= */}
      <section id="top" className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 left-1/3 w-[700px] h-[700px] rounded-full bg-[#9D00FF]/15 blur-[140px]" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#FF1493]/10 blur-[120px]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-16 md:pb-24">
          <Link
            to="/"
            data-testid="back-to-hub"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-10"
          >
            <ArrowLeft className="w-4 h-4" />
            Toutes les études de cas
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <span className="font-mono-data text-[#FF1493] text-sm">
              ÉTUDE N° {cs.number}
            </span>
            <span className="w-10 h-px bg-white/20" />
            <span className="ls-overline">{cs.published_at}</span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-4xl md:text-6xl lg:text-[76px] leading-[0.98] tracking-[-0.03em] max-w-[16ch]"
          >
            {cs.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 text-xl md:text-2xl text-white/60 font-serif-display italic max-w-3xl leading-snug"
          >
            {cs.subtitle}
          </motion.p>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl border-t border-white/10 pt-8">
            <Meta label="Client" value={cs.client_type} />
            <Meta label="Durée" value={cs.duration} />
            <Meta label="Budget" value={cs.budget} />
            <Meta label="Canaux" value={cs.channels.join(" · ")} />
          </div>
        </div>
      </section>

      {/* ========= KPI HERO STRIP ========= */}
      <section className="relative border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="flex items-baseline gap-4 mb-10">
            <Sparkles className="w-4 h-4 text-[#FF1493]" />
            <span className="ls-overline text-[#FF1493]">En un coup d'œil</span>
          </div>
          <div
            className={`grid grid-cols-2 md:grid-cols-3 ${
              (cs.headline_kpis || []).length >= 6
                ? "lg:grid-cols-6"
                : "lg:grid-cols-3"
            } gap-px bg-white/10`}
          >
            {(cs.headline_kpis || []).map((k) => (
              <KpiCell
                key={k.label}
                label={k.label}
                value={k.value}
                format={k.format}
                prefix={k.prefix || ""}
                suffix={k.suffix || ""}
                accent={!!k.accent}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ========= BODY WITH STICKY TOC ========= */}
      <section className="relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <StickyToc items={TOC} />

          <div className="lg:col-span-9 space-y-28">
            {/* --- 01 Contexte --- */}
            <Chapter id="contexte" num="01" title="Contexte">
              <p className="text-lg md:text-xl text-white/75 leading-relaxed max-w-3xl">
                {cs.context}
              </p>
            </Chapter>

            {/* --- 02 Objectifs --- */}
            <Chapter id="objectifs" num="02" title="Objectifs marketing">
              <p className="text-white/60 mb-8 max-w-2xl">
                La campagne visait trois objectifs complémentaires, conçus pour s'alimenter mutuellement.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                {cs.objectives.map((o, i) => {
                  const Icon = [Target, TrendingUp, GraduationCap][i];
                  return (
                    <motion.div
                      key={o.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className="group rounded-2xl border border-white/10 p-6 bg-[#0a0a0a] hover:border-[#9D00FF]/60 transition"
                    >
                      <div className="flex items-center justify-between mb-6">
                        <div className="font-mono-data text-xs text-[#FF1493]">0{i + 1}</div>
                        <Icon className="w-5 h-5 text-white/40 group-hover:text-[#FF1493] transition" />
                      </div>
                      <div className="font-serif-display text-2xl mb-3">{o.title}</div>
                      <div className="text-sm text-white/60 leading-relaxed">{o.desc}</div>
                    </motion.div>
                  );
                })}
              </div>
            </Chapter>

            {/* --- 03 Stratégie --- */}
            <Chapter id="strategie" num="03" title="Stratégie déployée">
              <div className="mb-10">
                <div className="ls-overline mb-3">Positionnement du message</div>
                <h3 className="font-serif-display text-2xl md:text-3xl leading-tight max-w-2xl">
                  {cs.positioning.headline}
                </h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {cs.positioning.pillars.map((p, i) => (
                  <div
                    key={p.title}
                    className="rounded-2xl border border-white/10 p-7 bg-gradient-to-br from-[#0f0f14] to-[#050505] relative overflow-hidden"
                  >
                    <div
                      className="absolute top-0 left-0 w-1 h-full"
                      style={{ background: CHART_COLORS[i] }}
                    />
                    <div className="ls-overline mb-4">Promesse {i + 1}</div>
                    <div className="font-serif-display text-2xl mb-3">{p.title}</div>
                    <div className="text-sm text-white/60 leading-relaxed">{p.desc}</div>
                  </div>
                ))}
              </div>

              <div className="mt-10 p-6 rounded-xl border border-white/10 bg-[#0a0a0a] text-white/70 text-sm leading-relaxed">
                <span className="text-[#FF1493]">→</span>{" "}
                Cette approche a permis de couvrir l'ensemble du funnel —{" "}
                <span className="text-white">découverte</span> →{" "}
                <span className="text-white">considération</span> →{" "}
                <span className="text-white">conversion</span>.
              </div>
            </Chapter>

            {/* --- 04 Dispositif (Channels & formats) --- */}
            <Chapter id="dispositif" num="04" title="Dispositif — canaux, formats, volume">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <div className="ls-overline mb-4">Canaux activés</div>
                  <ul className="space-y-5">
                    {cs.channels_formats.map((c) => {
                      const Icon = {
                        YouTube: Youtube,
                        Twitch: Twitch,
                        Instagram: Instagram,
                      }[c.channel];
                      return (
                        <li key={c.channel} className="flex gap-4 border-t border-white/10 pt-5">
                          <div className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center flex-shrink-0">
                            {Icon && <Icon className="w-4 h-4 text-[#FF1493]" />}
                          </div>
                          <div>
                            <div className="font-serif-display text-xl">{c.channel}</div>
                            <div className="ls-overline text-white/50 mb-2">{c.role}</div>
                            <div className="text-sm text-white/60">{c.formats.join(" · ")}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="grid grid-cols-2 gap-3 content-start">
                  <DispositifStat label="Influenceurs" value={cs.dispositif.influencers} />
                  <DispositifStat label="Activations" value={cs.dispositif.activations} />
                  <DispositifStat
                    label="Durée"
                    value={cs.dispositif.duration_months}
                    suffix=" mois"
                  />
                  <DispositifStat
                    label="Budget"
                    value={cs.dispositif.budget_usd}
                    format="number"
                    suffix={` ${cs.currency || "$"}`}
                  />
                </div>
              </div>
            </Chapter>

            {/* --- 05 Performance globale (Charts) --- */}
            <Chapter id="performance" num="05" title="Performance globale">
              <p className="text-white/70 mb-10 max-w-2xl">
                Les {cs.dispositif?.influencers} influenceurs, {cs.dispositif?.activations} activations et{" "}
                {cs.dispositif?.duration_months} mois combinés se lisent en un coup d'œil.
              </p>

              <div className="grid lg:grid-cols-5 gap-6">
                <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
                  <div className="mb-4">
                    <div className="ls-overline mb-1">Vues cumulées</div>
                    <div className="font-serif-display text-xl">Par influenceur</div>
                  </div>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={cs.influencers.map((i) => ({
                          name: `#${i.id} · ${i.channel}`,
                          Vues: i.views,
                        }))}
                        margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="name" stroke="#71717A" tick={{ fontSize: 10 }} />
                        <YAxis
                          stroke="#71717A"
                          tickFormatter={(v) =>
                            v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                          }
                        />
                        <Tooltip
                          formatter={(v) =>
                            v.toLocaleString("fr-FR").replace(/,/g, " ")
                          }
                          contentStyle={{
                            background: "#0a0a0a",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 8,
                          }}
                        />
                        <Bar dataKey="Vues" radius={[4, 4, 0, 0]}>
                          {cs.influencers.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
                  <div className="mb-4">
                    <div className="ls-overline mb-1">CPL</div>
                    <div className="font-serif-display text-xl">Coût par lead par profil</div>
                  </div>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        layout="vertical"
                        data={cs.influencers.map((i) => ({
                          name: `#${i.id}`,
                          CPL: i.cpl,
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis type="number" stroke="#71717A" />
                        <YAxis type="category" dataKey="name" stroke="#71717A" width={40} />
                        <Tooltip
                          formatter={(v) => `${v.toFixed(2)} $`}
                          contentStyle={{
                            background: "#0a0a0a",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 8,
                          }}
                        />
                        <Bar dataKey="CPL" radius={[0, 4, 4, 0]}>
                          {cs.influencers.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
                  <div className="mb-4">
                    <div className="ls-overline mb-1">Répartition leads</div>
                    <div className="font-serif-display text-xl">Poids de chaque levier</div>
                  </div>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={cs.influencers.map((i) => ({
                            name: `#${i.id} · ${i.channel}`,
                            value: i.leads,
                          }))}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          stroke="#050505"
                          strokeWidth={2}
                        >
                          {cs.influencers.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            background: "#0a0a0a",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 8,
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
                  <div className="mb-4">
                    <div className="ls-overline mb-1">Récapitulatif</div>
                    <div className="font-serif-display text-xl">Les chiffres bruts</div>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {cs.global_kpis_table.map((row, i) => (
                        <tr key={i} className="border-b border-white/5 last:border-0">
                          <td className="py-2.5 text-white/70">{row.kpi}</td>
                          <td className="py-2.5 text-right font-mono-data text-white">
                            {row.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-5 p-4 rounded-lg bg-[#FF1493]/10 border border-[#FF1493]/20 text-sm text-white/90 leading-relaxed">
                    <span className="text-[#FF1493]">→</span> Revenu estimé{" "}
                    <span className="font-mono-data text-white">
                      {formatMoney(
                        cs.revenue_summary?.revenue_estimated_usd,
                        cs.currency || "$"
                      )}
                    </span>{" "}
                    pour un investissement de{" "}
                    <span className="font-mono-data text-white">
                      {formatMoney(
                        cs.revenue_summary?.investment_usd,
                        cs.currency || "$"
                      )}
                    </span>
                    .
                  </div>
                </div>
              </div>
            </Chapter>

            {/* --- 06 Analyse détaillée par influenceur --- */}
            <Chapter id="influenceurs" num="06" title="Analyse détaillée par influenceur">
              <div className="space-y-4">
                {cs.influencers.map((inf, i) => (
                  <InfluencerRow
                    key={inf.id}
                    inf={inf}
                    accent={CHART_COLORS[i]}
                    currency={cs.currency || "$"}
                  />
                ))}
              </div>
            </Chapter>

            {/* --- 07 Insights (light editorial break) --- */}
          </div>
        </div>
      </section>

      {/* Light Editorial Section — break rhythm */}
      <section id="insights" className="editorial-light relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="flex items-baseline gap-4 mb-4">
            <span className="font-mono-data text-sm text-[#9D00FF]">07</span>
            <span className="ls-overline">Insights data & apprentissages</span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.98] tracking-[-0.02em] max-w-3xl mb-16">
            Quatre enseignements à graver dans la prochaine campagne.
          </h2>

          <div className="grid md:grid-cols-2 gap-x-10 gap-y-14">
            {cs.insights.map((ins, i) => (
              <motion.div
                key={ins.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="grid grid-cols-[auto,1fr] gap-6 border-t border-black/10 pt-8"
              >
                <div className="font-serif-display text-6xl text-[#FF1493] leading-none">
                  {ins.number}
                </div>
                <div>
                  <h3 className="font-serif-display text-2xl md:text-3xl leading-tight mb-3">
                    {ins.title}
                  </h3>
                  <div className="inline-block font-mono-data text-xs px-3 py-1 rounded-full bg-black text-white mb-4">
                    {ins.stat}
                  </div>
                  <p className="text-base text-black/70 leading-relaxed">{ins.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Back to dark — Impact + Reco + Conclusion */}
      <section className="relative border-t border-white/10 bg-[#050505]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-9 lg:col-start-4 space-y-28">
            {/* --- 08 Impact business --- */}
            <Chapter id="impact" num="08" title="Impact business">
              <div className="grid md:grid-cols-4 gap-4 mb-8">
                {Array.isArray(cs.business_impact?.cards) ? (
                  cs.business_impact.cards.map((c) => (
                    <ImpactCard key={c.label} {...c} />
                  ))
                ) : (
                  <>
                    <ImpactCard
                      label="Leads qualifiés"
                      value={cs.business_impact?.leads || 0}
                      format="number"
                    />
                    <ImpactCard
                      label="Clients payants"
                      value={cs.business_impact?.paying_users || 0}
                      format="number"
                    />
                    <ImpactCard
                      label="MRR estimé"
                      value={cs.business_impact?.mrr_usd || 0}
                      format="number"
                      suffix={` ${cs.currency || "$"}/mo`}
                      accent
                    />
                    <ImpactCard
                      label="Rentabilité"
                      value={cs.business_impact?.payback_months || "—"}
                      raw
                    />
                  </>
                )}
              </div>
              <p className="text-white/70 text-lg leading-relaxed max-w-3xl">
                {cs.business_impact?.summary}
              </p>
            </Chapter>

            {/* --- 09 Recommandations --- */}
            <Chapter id="recommandations" num="09" title="Recommandations — phase 2">
              <p className="text-white/60 mb-10 max-w-2xl">
                Cinq axes pour capitaliser sur la traction acquise et renforcer la rentabilité.
              </p>
              <ol className="space-y-0">
                {cs.recommendations.map((r, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="group grid grid-cols-[60px,1fr,auto] items-center gap-6 border-t border-white/10 py-6 hover:bg-white/[0.02] transition px-4 -mx-4 rounded"
                  >
                    <div className="font-mono-data text-sm text-[#FF1493]">0{i + 1}</div>
                    <div className="font-serif-display text-xl md:text-2xl">{r}</div>
                    <Check className="w-4 h-4 text-white/30 group-hover:text-[#FF1493] transition" />
                  </motion.li>
                ))}
              </ol>
            </Chapter>

            {/* --- 10 Conclusion --- */}
            <Chapter id="conclusion" num="10" title="Conclusion">
              <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#0f0f14] to-[#050505] p-8 md:p-12">
                <div className="absolute top-6 right-6 font-serif-display text-8xl text-white/5 leading-none">
                  "
                </div>
                <p className="font-serif-display text-2xl md:text-[34px] leading-[1.2] tracking-[-0.01em] max-w-3xl">
                  {cs.conclusion_hook || cs.conclusion}
                </p>
                <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-3xl">
                  {cs.conclusion}
                </p>
              </div>
            </Chapter>
          </div>
        </div>
      </section>

      {/* Contact CTA — email only */}
      <section id="contact" className="relative border-t border-white/10 bg-[#050505]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-7">
              <div className="ls-overline text-[#FF1493] mb-4">Parlons de votre campagne</div>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.98] tracking-[-0.02em] mb-6">
                Un produit à faire décoller ?
              </h2>
              <p className="text-white/70 text-lg leading-relaxed max-w-xl mb-10">
                Racontez-nous votre projet. Nous revenons vers vous avec un diagnostic rapide et des pistes de dispositif.
              </p>
              <a
                href="mailto:contact@linksync.fr?subject=Nouveau%20projet%20LinkSync"
                data-testid="contact-email-cta"
                className="group inline-flex items-center gap-3 h-14 pl-6 pr-7 rounded-full bg-white text-black text-base font-medium hover:bg-[#FF1493] hover:text-white transition"
              >
                <span>Écrire à contact@linksync.fr</span>
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
              </a>
            </div>
            <div className="md:col-span-5">
              <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-8">
                <div className="ls-overline mb-5 text-white/70">Coordonnées</div>
                <ul className="space-y-4 text-sm">
                  <li className="flex flex-col gap-1">
                    <span className="text-white/50">Email</span>
                    <a href="mailto:contact@linksync.fr" className="text-white ls-link text-base">contact@linksync.fr</a>
                  </li>
                  <li className="flex flex-col gap-1">
                    <span className="text-white/50">Site</span>
                    <a href="https://linksync.fr" target="_blank" rel="noreferrer" className="text-white ls-link text-base">linksync.fr</a>
                  </li>
                  <li className="flex flex-col gap-1">
                    <span className="text-white/50">Basés à</span>
                    <span className="text-white text-base">Paris · Remote</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingPdfButton slug={cs.slug} />
    </div>
  );
}

/* ========== Helpers ========== */

function Chapter({ id, num, title, children }) {
  return (
    <section id={id} data-testid={`chapter-${id}`} className="scroll-mt-24">
      <div className="mb-8 flex items-baseline gap-4">
        <span className="font-mono-data text-sm text-[#FF1493]">{num}</span>
        <span className="ls-overline">{title}</span>
      </div>
      <h2 className="font-serif-display text-3xl md:text-5xl leading-[0.98] tracking-[-0.02em] mb-10 max-w-3xl">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="ls-overline mb-2">{label}</div>
      <div className="text-white font-mono-data text-sm">{value}</div>
    </div>
  );
}

function formatMoney(n, currency = "$") {
  if (typeof n !== "number") return "—";
  return n.toLocaleString("fr-FR").replace(/,/g, " ") + " " + currency;
}

function KpiCell({ label, value, format, prefix, suffix, accent }) {
  return (
    <div className="p-5 md:p-7 bg-[#050505] flex flex-col justify-between min-h-[140px]">
      <div className="ls-overline mb-4 text-white/60">{label}</div>
      <CountUp
        testId={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
        value={value}
        format={format}
        prefix={prefix}
        suffix={suffix}
        className={`text-3xl md:text-4xl lg:text-[44px] font-medium tracking-tight leading-none whitespace-nowrap ${accent ? "text-[#FF1493]" : "text-white"}`}
      />
    </div>
  );
}

function DispositifStat({ label, value, format = "number", suffix = "" }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-5">
      <div className="ls-overline mb-3">{label}</div>
      <CountUp
        value={value}
        format={format}
        suffix={suffix}
        className="text-3xl md:text-4xl font-medium tracking-tight"
      />
    </div>
  );
}

function ImpactCard({ label, value, format = "number", prefix = "", suffix = "", accent, raw }) {
  return (
    <div
      className={`rounded-xl border p-6 ${accent ? "border-[#FF1493]/40 bg-[#FF1493]/5" : "border-white/10 bg-[#0a0a0a]"}`}
    >
      <div className="ls-overline mb-4">{label}</div>
      {raw ? (
        <div className="font-mono-data text-3xl md:text-4xl">{value}</div>
      ) : (
        <CountUp
          value={value}
          format={format}
          prefix={prefix}
          suffix={suffix}
          className={`text-3xl md:text-4xl font-medium ${accent ? "text-[#FF1493]" : "text-white"}`}
        />
      )}
    </div>
  );
}

function InfluencerRow({ inf, accent, currency = "$" }) {
  const ChannelIcon = {
    YouTube: Youtube,
    Twitch: Twitch,
    Instagram: Instagram,
  }[inf.channel];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5 }}
      data-testid={`influencer-row-${inf.id}`}
      className="relative rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 md:p-8 overflow-hidden"
    >
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 mb-3">
            {ChannelIcon && <ChannelIcon className="w-4 h-4" style={{ color: accent }} />}
            <span
              className="text-[11px] uppercase tracking-[0.22em] font-medium"
              style={{ color: accent }}
            >
              {inf.channel} · {inf.tier}
            </span>
          </div>
          <div className="font-serif-display text-2xl md:text-3xl mb-2">
            {inf.name}
          </div>
          <div className="text-sm text-white/70 leading-relaxed max-w-xl">
            {inf.positioning}
          </div>
          <div className="mt-2 font-mono-data text-xs text-white/50">
            Audience · {inf.audience}
          </div>
        </div>
        <div className="shrink-0 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] rounded-full border border-white/15 px-3 py-1.5 text-white/80">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
          {inf.highlight}
        </div>
      </div>

      <div className="text-xs text-white/60 mb-4 font-mono-data">
        Format · {inf.format_label}
      </div>

      {/* Campaign setup */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-2 mb-3">
        <MetricPill label="Opérations" value={inf.operations} />
        <MetricPill label="Durée" value={`${inf.duration_months} mois`} raw />
        <MetricPill
          label="Budget"
          value={inf.budget_usd}
          format="number"
          suffix={` ${currency}`}
        />
      </div>

      {/* Performance */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <MetricPill label="Vues" value={inf.views} format="compact" />
        {inf.engagement_rate ? (
          <MetricPill label="Engagement" value={inf.engagement_rate} raw />
        ) : (
          <MetricPill label="Engagement" value="—" raw dim />
        )}
        <MetricPill label="TCV" value={inf.tcv} raw />
        <MetricPill label="Clics" value={inf.clicks} format="number" />
        <MetricPill label="Leads" value={inf.leads} format="number" />
        <MetricPill
          label="CPL"
          value={inf.cpl}
          format="decimal"
          suffix={` ${currency}`}
          highlight
        />
      </div>

      <div className="mt-5 pt-5 border-t border-white/10 grid lg:grid-cols-[auto,1fr] gap-6 items-start">
        <div className="flex items-center gap-3 shrink-0">
          <span className="ls-overline text-white/50">ROI</span>
          <span className="font-mono-data text-2xl" style={{ color: accent }}>
            ×{inf.roi_mult.toString().replace(".", ",")}
          </span>
        </div>
        <p className="text-sm text-white/75 leading-relaxed">{inf.analysis}</p>
      </div>
    </motion.div>
  );
}

function MetricPill({ label, value, format, suffix, raw, highlight, dim }) {
  return (
    <div className="rounded-lg bg-[#050505] border border-white/10 px-3 py-2.5 flex flex-col">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">
        {label}
      </div>
      {raw ? (
        <span
          className={`font-mono-data text-base leading-tight ${dim ? "text-white/40" : "text-white"}`}
        >
          {value}
        </span>
      ) : (
        <CountUp
          value={value}
          format={format}
          suffix={suffix}
          className={`text-base leading-tight ${highlight ? "text-[#FF1493]" : "text-white"}`}
        />
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-32 text-center text-white/40">
        Chargement de l'étude de cas…
      </div>
    </div>
  );
}

function NotFoundState() {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <div className="ls-overline text-[#FF1493] mb-6">404 — Introuvable</div>
        <h1 className="font-serif-display text-5xl mb-6">Cette étude de cas n'existe pas.</h1>
        <Link to="/" className="ls-link text-white/80">← Retour au hub</Link>
      </div>
    </div>
  );
}
